import { Controller } from "@hotwired/stimulus"

// 時系列 item の発生時点を 1 個の入力欄 (YYYY/MM/DD HH:MM:SS・右ほど任意) で入力する補助 (§11.3)。
// - 全角数字/区切り→半角を即時正規化する
// - 入力した最深の桁から「保存される精度」をライブ表示する (aria-live)
// - 上位桁が空のまま下位桁が入る「途中省略」を警告する
// - プレビュータブ用に「日付ラベル + 本文」を更新する
// 正本の parse・precision 再導出はサーバ (PageTimeline::TimeParser.parse_input)。JS 無効でもサーバが parse する。
export default class extends Controller {
  static targets = ["combined", "preview", "bodyInput", "previewDate", "previewBody"]

  static LEVELS = [
    { unit: "年単位" },
    { unit: "月単位" },
    { unit: "日単位" },
    { unit: "時単位" },
    { unit: "分単位" },
    { unit: "秒単位" },
  ]

  connect() {
    this.update()
  }

  onInput(event) {
    if (event && event.target === this.combinedTargetOrNull()) {
      event.target.value = this.normalize(event.target.value)
    }
    this.update()
  }

  combinedTargetOrNull() {
    return this.hasCombinedTarget ? this.combinedTarget : null
  }

  normalize(value) {
    return value
      .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
      .replace(/／/g, "/")
      .replace(/：/g, ":")
      .replace(/　/g, " ")
  }

  // 入力欄を [年, 月, 日, 時, 分, 秒] の文字列配列に分解する (未入力は "")。
  parts() {
    if (!this.hasCombinedTarget) return ["", "", "", "", "", ""]

    const value = this.normalize(this.combinedTarget.value).trim()
    const segments = value.split(/\s+/)
    const [year, month, day] = (segments[0] || "").split("/")
    const [hour, min, sec] = (segments[1] || "").split(":")
    return [year, month, day, hour, min, sec].map((v) => (v || "").trim())
  }

  update() {
    const parts = this.parts()
    const lastFilled = parts.reduce((acc, v, i) => (v !== "" ? i : acc), -1)

    this.updatePrecision(parts, lastFilled)
    this.updatePreviewRow(parts, lastFilled)
  }

  updatePrecision(parts, lastFilled) {
    if (!this.hasPreviewTarget) return

    if (lastFilled < 0) {
      this.setPreview("発生時点を入力してください（例: 2016/3/15 14:30:00）")
    } else if (parts.slice(0, lastFilled).some((v) => v === "")) {
      this.setPreview("⚠ 上位の桁（年→月→日→時→分→秒）を空けずに入力してください", true)
    } else {
      this.setPreview(`保存される精度: ${this.dateLabel(parts, lastFilled)}（${this.constructor.LEVELS[lastFilled].unit}）`)
    }
  }

  setPreview(text, warning = false) {
    this.previewTarget.textContent = text
    this.previewTarget.classList.toggle("text-red-600", warning)
  }

  // テーブル行と同じ「日付ラベル」を組み立てる (PageTimelineItem#date_label 相当)。
  dateLabel(parts, lastFilled) {
    if (lastFilled < 0) return ""

    const [y, mo, d, h, mi, s] = parts
    const n = (v) => String(Number(v))
    const pad = (v) => String(Number(v)).padStart(2, "0")
    let label = `${n(y)}年`
    if (lastFilled >= 1) label += `${n(mo)}月`
    if (lastFilled >= 2) label += `${n(d)}日`
    if (lastFilled === 3) label += ` ${n(h)}時`
    if (lastFilled === 4) label += ` ${pad(h)}:${pad(mi)}`
    if (lastFilled >= 5) label += ` ${pad(h)}:${pad(mi)}:${pad(s)}`
    return label
  }

  updatePreviewRow(parts, lastFilled) {
    if (this.hasPreviewDateTarget) {
      this.previewDateTarget.textContent = lastFilled < 0 ? "（発生時点 未入力）" : this.dateLabel(parts, lastFilled)
    }
    if (this.hasPreviewBodyTarget) {
      this.previewBodyTarget.textContent = this.hasBodyInputTarget ? this.bodyInputTarget.value : ""
    }
  }
}
